<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import { formatFullDateTimeSP } from '../../agenda/agendaDateUtils';
import { formatPhoneDisplay } from '../constants/phoneFormat';

const props = defineProps({
  card: { type: Object, required: true },
  column: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const { avatarColor, initials } = useAvatarColor();

const allAgents = computed(() => store.getters['agents/getAgents'] ?? []);

const watcherAgent = watcherId => allAgents.value.find(a => a.id === watcherId);

const stageName = computed(() => {
  const columns = props.card.columns ?? [];
  return columns.find(c => c.id === props.card.column_id)?.name ?? '';
});

const customValue = computed(() => {
  if (!props.column.isCustom) return null;
  return props.card.custom_attributes?.[props.column.key] ?? null;
});

const formattedValue = computed(() => {
  if (
    props.card.value === null ||
    props.card.value === undefined ||
    props.card.value === ''
  ) {
    return null;
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(props.card.value));
});

function formatClosingDate(date) {
  if (!date) return '';
  return new Date(`${date}T00:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const taskCountClass = computed(() => {
  const pending =
    props.card.task_alert !== null && props.card.task_alert !== undefined;
  return pending ? 'text-n-slate-12' : 'text-n-slate-10 opacity-60';
});

const TAGS_VISIBLE_LIMIT = 2;
const allTags = computed(() => props.card.contact?.labels ?? []);
const visibleTags = computed(() => allTags.value.slice(0, TAGS_VISIBLE_LIMIT));
const hiddenTagsCount = computed(() =>
  Math.max(0, allTags.value.length - TAGS_VISIBLE_LIMIT)
);
const hiddenTagsTooltip = computed(() =>
  allTags.value
    .slice(TAGS_VISIBLE_LIMIT)
    .map(tag => tag.replace(/</g, '&lt;').replace(/>/g, '&gt;'))
    .join('<br>')
);
</script>

<template>
  <span
    v-if="column.key === 'title'"
    class="truncate font-medium text-n-slate-12"
  >
    {{ card.title }}
  </span>

  <div
    v-else-if="column.key === 'contact'"
    class="flex items-center gap-2 min-w-0"
  >
    <div
      v-if="card.contact?.name"
      class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
      :style="{ backgroundColor: avatarColor(card.contact.name) }"
    >
      {{ initials(card.contact.name) }}
    </div>
    <span class="truncate">{{ card.contact?.name ?? '' }}</span>
  </div>

  <span v-else-if="column.key === 'company'" class="truncate">
    {{ card.contact?.company?.name ?? '' }}
  </span>

  <span v-else-if="column.key === 'phone_number'" class="truncate">
    {{ formatPhoneDisplay(card.contact?.phone_number) }}
  </span>

  <span v-else-if="column.key === 'email'" class="truncate">
    {{ card.contact?.email ?? '' }}
  </span>

  <span v-else-if="column.key === 'city'" class="truncate">
    {{ card.contact?.additional_attributes?.city ?? '' }}
  </span>

  <span v-else-if="column.key === 'opp_source'" class="truncate">
    {{ card.custom_attributes?.opp_source ?? '' }}
  </span>

  <span v-else-if="column.key === 'stage'" class="truncate">
    {{ stageName }}
  </span>

  <span
    v-else-if="column.key === 'closing_date'"
    class="truncate whitespace-nowrap"
  >
    {{ formatClosingDate(card.closing_date) }}
  </span>

  <span v-else-if="column.key === 'value'" class="truncate whitespace-nowrap">
    {{ formattedValue }}
  </span>

  <div
    v-else-if="column.key === 'assignee'"
    class="flex items-center gap-2 min-w-0"
  >
    <div
      v-if="card.assignee"
      class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
      :style="
        card.assignee.thumbnail
          ? {
              backgroundImage: `url(${card.assignee.thumbnail})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : { backgroundColor: avatarColor(card.assignee) }
      "
    >
      <span v-if="!card.assignee.thumbnail">{{
        initials(card.assignee.name)
      }}</span>
    </div>
    <span class="truncate">{{ card.assignee?.name ?? '' }}</span>
  </div>

  <span v-else-if="column.key === 'opp_products'" class="truncate">
    {{ (card.custom_attributes?.opp_products ?? []).join(', ') }}
  </span>

  <div v-else-if="column.key === 'watchers'" class="flex -space-x-1">
    <div
      v-for="watcherId in card.custom_attributes?.watchers ?? []"
      :key="watcherId"
      class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white border border-n-background"
      :style="
        watcherAgent(watcherId)?.thumbnail
          ? {
              backgroundImage: `url(${watcherAgent(watcherId).thumbnail})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : {
              backgroundColor: avatarColor(
                watcherAgent(watcherId) ?? String(watcherId)
              ),
            }
      "
    >
      <span
        v-if="watcherAgent(watcherId) && !watcherAgent(watcherId).thumbnail"
      >
        {{ initials(watcherAgent(watcherId).name) }}
      </span>
    </div>
  </div>

  <div v-else-if="column.key === 'tags'" class="flex flex-nowrap gap-1">
    <span
      v-for="tag in visibleTags"
      :key="tag"
      class="text-xs px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11 whitespace-nowrap flex-shrink-0"
    >
      {{ tag }}
    </span>
    <span
      v-if="hiddenTagsCount > 0"
      v-tooltip="{ content: hiddenTagsTooltip, html: true }"
      class="text-xs px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11 whitespace-nowrap flex-shrink-0"
    >
      +{{ hiddenTagsCount }}
    </span>
  </div>

  <span v-else-if="column.key === 'task_count'" :class="taskCountClass">
    {{ card.task_count > 0 ? card.task_count : t('KANBAN.LIST_VIEW.NO_TASKS') }}
  </span>

  <span
    v-else-if="column.key === 'next_meeting'"
    class="truncate whitespace-nowrap"
  >
    <template v-if="card.next_meeting">
      {{ card.next_meeting.title }} —
      {{
        formatFullDateTimeSP(
          new Date(card.next_meeting.due_date * 1000).toISOString()
        )
      }}
    </template>
    <template v-else>{{ t('KANBAN.LIST_VIEW.NO_MEETING') }}</template>
  </span>

  <span v-else-if="column.isCustom" class="truncate">
    {{ customValue }}
  </span>
</template>
